/**
 * Impersonation engine — role registry.
 *
 * THE ONLY PLACE role-specific behavior lives. For each `ImpersonationKind` it
 * declares, as pure functions of the target:
 *   - `entryRoute`  : where to navigate when this layer becomes active.
 *   - `routePrefix` : the URL prefix that defines "still inside this layer's tree"
 *                     (used by the provider's nav-exit guard).
 *   - `headers`     : the HTTP headers this layer contributes to GraphQL calls.
 *   - `theme`       : optional theme behavior ("firm" => swap to the firm's branding).
 *
 * Adding a new role later = one new entry in this object. Nothing else changes.
 *
 * Counterpart to `ImpersonationTarget` in `types.ts`:
 *   - ImpersonationTarget = "for this role, what ids/fields must the CALLER supply."
 *   - ROLE_REGISTRY       = "for this role, what's the entry route / route prefix /
 *                            headers / theme" (i.e. what the ENGINE does with them).
 *
 * Route/header values mirror the backend contract. If BE expects different
 * fields per kind, this is the only file that changes.
 */
import { ROLE_CHOICES, RoleType } from "@/shared/lib/types";
import type { ImpersonationKind, ImpersonationTarget } from "./types";

/** Per-role behavior spec. `T` is narrowed to the specific target variant for this kind. */
export type RoleSpec<T extends ImpersonationTarget> = {
  /** Where to navigate on enter. */
  entryRoute: (t: T) => string;
  /**
   * URL prefix(es) used to detect whether we're still inside this layer's route tree.
   * A string array allows roles (like enterprise_admin) whose valid routes span multiple
   * top-level path trees (e.g. `/enterprise/…` AND `/reports/…`).
   */
  routePrefix: (t: T) => string | string[];
  /** Headers this layer contributes (merged bottom→top; see storage.getImpersonationHeaders). */
  headers: (t: T) => Record<string, string>;
  /**
   * Optional theme swap. "firm" => apply the firm's branding while this layer is
   * active (advisor / firm_admin / enterprise_admin are inside a firm and should
   * look the way that user sees the app). Omitted => inherit the current theme.
   */
  theme?: "firm" | "default";
};

export const ROLE_REGISTRY = {
  [ROLE_CHOICES[RoleType.HeadOfTheFamily]]: {
    entryRoute: (t) => `/client/${t.familyId}/overview`,
    routePrefix: (t) => `/client/${t.familyId}`,
    // `userId` may be 0 when an advisor enters with only the familyId — the HOH
    // id is filled in later via `setImpersonatedUserId` once the client page
    // resolves it. Omit `impersonate` until it's known so we never send
    // `impersonate: "0"`; this matches the legacy "family header only until the
    // client id is known" behavior. The `family` header carries identity meanwhile.
    headers: (t) => ({
      ...(t.userId ? { impersonate: String(t.userId) } : {}),
      family: String(t.familyId),
    }),
  },
  [ROLE_CHOICES[RoleType.FamilyMember]]: {
    entryRoute: (t) => `/family/${t.familyId}/overview`,
    routePrefix: (t) => `/family/${t.familyId}`,
    // See `head_of_the_family`: `impersonate` is omitted until a concrete `userId` is known.
    headers: (t) => ({
      ...(t.userId ? { impersonate: String(t.userId) } : {}),
      family: String(t.familyId),
    }),
  },
  [ROLE_CHOICES[RoleType.FinancialAdvisor]]: {
    entryRoute: () => "/advisor/overview",
    routePrefix: () => "/advisor",
    headers: (t) => ({ impersonate: String(t.userId) }),
    theme: "firm",
  },
  [ROLE_CHOICES[RoleType.RIA]]: {
    // NOTE: `/firm/[id]` has no `/overview` subroute, so enter the firm page itself.
    entryRoute: (t) => `/firm/${t.firmId}`,
    routePrefix: (t) => `/firm/${t.firmId}`,
    headers: (t) => ({ impersonate: String(t.userId), firm: String(t.firmId) }),
    theme: "firm",
  },
  [ROLE_CHOICES[RoleType.EnterpriseAdmin]]: {
    entryRoute: (t) => `/enterprise/${t.enterpriseId}`,
    // Enterprise admins can navigate to /reports/* in addition to /enterprise/* — both trees
    // must be allowed or the nav-exit guard snaps them back when they open Reports.
    routePrefix: (t) => [`/enterprise/${t.enterpriseId}`, "/reports"],
    headers: (t) => ({ impersonate: String(t.userId) }),
    theme: "firm",
  },
  [ROLE_CHOICES[RoleType.CorporateAdmin]]: {
    entryRoute: (t) => `/corporate-admin/${t.familyId}/overview`,
    routePrefix: (t) => `/corporate-admin/${t.familyId}`,
    // See `head_of_the_family`: `impersonate` is omitted until a concrete `userId` is known.
    headers: (t) => ({
      ...(t.userId ? { impersonate: String(t.userId) } : {}),
      family: String(t.familyId),
    }),
  },
  [ROLE_CHOICES[RoleType.SelfDirected]]: {
    entryRoute: (t) => `/participant/${t.familyId}/overview`,
    routePrefix: (t) => `/participant/${t.familyId}`,
    // See `head_of_the_family`: `impersonate` is omitted until a concrete `userId` is known.
    headers: (t) => ({
      ...(t.userId ? { impersonate: String(t.userId) } : {}),
      family: String(t.familyId),
    }),
  },
  /*
   * What this `satisfies` clause means (read it as one sentence):
   * "This object must have exactly one entry per impersonation role, and each
   *  entry's route/header functions must accept precisely the fields that role's
   *  ImpersonationTarget declares."
   *
   * Breaking down the type, piece by piece:
   *
   *   [K in ImpersonationKind]
   *     A mapped type: for EVERY role K in the union
   *     ("head_of_the_family" | "financial_advisor" | "ria" | ...), the object must
   *     have a key K.
   *     → Forget a role (or misspell one) and TypeScript errors. Guarantees the
   *       registry covers all roles.
   *
   *   Extract<ImpersonationTarget, { kind: K }>
   *     ImpersonationTarget is the union of all 7 target shapes. Extract keeps
   *     only the member whose `kind` equals the current K — i.e. it narrows the
   *     big union down to THIS role's target. Example, when K = "financial_advisor":
   *       Extract<ImpersonationTarget, { kind: "financial_advisor" }>
   *         === { kind: "financial_advisor"; userId: number; firmId: number }
   *
   *   RoleSpec<...that target...>
   *     The per-role behavior whose functions RECEIVE that exact target. So for
   *     "financial_advisor", `entryRoute`/`headers` get an advisor target →
   *     `t.firmId` is valid and `t.familyId` is a compile error. For
   *     "head_of_the_family" it's the opposite. That's why
   *     `head_of_the_family.entryRoute: (t) => `/client/${t.familyId}/...`` type-checks
   *     but the same line under `financial_advisor` (which has no familyId) would not.
   *
   * Why `satisfies` and not a type annotation (`const ROLE_REGISTRY: {...} = ...`)?
   *   `satisfies` CHECKS the object against the type but keeps the precise inferred
   *   types, so e.g. `ROLE_REGISTRY.financial_advisor.theme` stays the literal "firm"
   *   (not the widened "firm" | "default" | undefined) and indexing stays narrow.
   *
   * Net effect: types.ts (required fields per role) and this file (behavior per
   * role) are provably kept in sync by the compiler.
   */
} satisfies {
  [K in ImpersonationKind]: RoleSpec<Extract<ImpersonationTarget, { kind: K }>>;
};
